/**
 * PricingCardsDark - Cartes pricing version dark theme
 */

import { Check, X, Sparkles, ArrowRight, Infinity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Configuration des plans avec les données EXACTES du prompt
const PLANS = [
  {
    id: "starter",
    name: "Starter",
    description: "Pour démarrer sereinement",
    priceMonthly: 49,
    priceYearly: 39,
    savings: 120,
    extraUserPrice: 15,
    features: [
      "5 chantiers actifs",
      "10 membres d'équipe",
      "60 analyses IA / mois",
      "100 scans IA / mois",
      "Historique 2 ans",
    ],
    hasExport: false,
    hasAPI: false,
    support: "Email (48h)",
    popular: false,
  },
  {
    id: "pro",
    name: "Pro",
    description: "Le plus populaire",
    priceMonthly: 99,
    priceYearly: 79,
    savings: 240,
    extraUserPrice: 12,
    features: [
      "15 chantiers actifs",
      "30 membres d'équipe",
      "240 analyses IA / mois",
      "450 scans IA / mois",
      "Historique 5 ans",
    ],
    hasExport: true,
    hasAPI: false,
    support: "Email + Chat (24h)",
    popular: true,
  },
  {
    id: "entreprise",
    name: "Entreprise",
    description: "Sans limites",
    priceMonthly: 149,
    priceYearly: 119,
    savings: 360,
    extraUserPrice: 10,
    features: [
      "Chantiers illimités",
      "Membres illimités",
      "Analyses IA illimitées",
      "Scans IA illimités",
      "Historique illimité",
    ],
    hasExport: true,
    hasAPI: true,
    support: "Chat prioritaire + Téléphone",
    popular: false,
  },
];

interface PricingCardsDarkProps {
  billingPeriod: "monthly" | "yearly";
  onSelectPlan: (planId: string) => void;
  loadingPlan?: string | null;
  currentPlan?: string;
}

export function PricingCardsDark({
  billingPeriod,
  onSelectPlan,
  loadingPlan,
  currentPlan,
}: PricingCardsDarkProps) {
  return (
    <section className="relative py-8">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {PLANS.map((plan, index) => {
            const isCurrentPlan = currentPlan === plan.id;
            const price = billingPeriod === "yearly" ? plan.priceYearly : plan.priceMonthly;
            const isPopular = plan.popular;

            return (
              <div
                key={plan.id}
                className={cn(
                  "relative rounded-2xl p-[1px] animate-fade-up",
                  isPopular
                    ? "bg-gradient-to-b from-[#D4AF37] via-[#D4AF37]/50 to-[#D4AF37]/10 md:scale-105 md:-my-4"
                    : "bg-gradient-to-b from-white/[0.1] to-transparent"
                )}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Badge Populaire */}
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                    <div className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#D4AF37] text-[#0A0A0F] text-sm font-bold shadow-lg shadow-[#D4AF37]/30">
                      <Sparkles className="h-4 w-4" />
                      Populaire
                    </div>
                  </div>
                )}

                <div
                  className={cn(
                    "rounded-2xl p-6 lg:p-8 h-full flex flex-col",
                    isPopular ? "bg-[#0F0F14]" : "bg-[#0F0F14]/80"
                  )}
                >
                  {/* Header */}
                  <div className="mb-6">
                    <p className="text-sm text-white/40 mb-1">{plan.description}</p>
                    <h3 className="text-2xl font-bold text-white mb-4">{plan.name}</h3>

                    {/* Prix */}
                    <div className="flex items-baseline gap-1">
                      {billingPeriod === "yearly" && (
                        <span className="text-lg text-white/30 line-through mr-2">
                          {plan.priceMonthly}€ HT
                        </span>
                      )}
                      <span
                        className={cn(
                          "text-4xl font-bold",
                          isPopular ? "text-[#F59E0B]" : "text-white"
                        )}
                      >
                        {price}€
                      </span>
                      <span className="text-white/40">HT /mois</span>
                    </div>

                    {/* Économie annuelle */}
                    {billingPeriod === "yearly" && (
                      <p className="text-sm text-[#10B981] mt-2 font-medium">
                        Économisez {plan.savings}€ HT/an
                      </p>
                    )}

                  </div>

                  {/* CTA */}
                  <Button
                    size="lg"
                    className={cn(
                      "w-full h-12 font-semibold rounded-xl transition-all duration-300 mb-6",
                      isPopular
                        ? "bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_4px_20px_rgba(245,158,11,0.3)]"
                        : "bg-transparent hover:bg-white/[0.05] text-white border border-white/[0.2] hover:border-white/[0.4]"
                    )}
                    onClick={() => onSelectPlan(plan.id)}
                    disabled={isCurrentPlan || loadingPlan === plan.id}
                  >
                    {loadingPlan === plan.id ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Chargement...
                      </span>
                    ) : isCurrentPlan ? (
                      "Plan actuel"
                    ) : (
                      <span className="flex items-center gap-2">
                        Commencer l'essai gratuit
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    )}
                  </Button>

                  {/* Features */}
                  <ul className="space-y-3 flex-1">
                    {plan.features.map((feature, i) => {
                      const isUnlimited = feature.includes("illimité");
                      return (
                        <li key={i} className="flex items-center gap-3">
                          <div
                            className={cn(
                              "h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0",
                              isPopular ? "bg-[#F59E0B]/10" : "bg-white/[0.05]"
                            )}
                          >
                            {isUnlimited ? (
                              <Infinity
                                className={cn(
                                  "h-3 w-3",
                                  isPopular ? "text-[#F59E0B]" : "text-white/50"
                                )}
                              />
                            ) : (
                              <Check
                                className={cn(
                                  "h-3 w-3",
                                  isPopular ? "text-[#F59E0B]" : "text-white/50"
                                )}
                              />
                            )}
                          </div>
                          <span className="text-sm text-white/60">{feature}</span>
                        </li>
                      );
                    })}

                    {/* Export données */}
                    <li className="flex items-center gap-3">
                      <div
                        className={cn(
                          "h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0",
                          plan.hasExport
                            ? isPopular
                              ? "bg-[#F59E0B]/10"
                              : "bg-white/[0.05]"
                            : "bg-white/[0.03]"
                        )}
                      >
                        {plan.hasExport ? (
                          <Check
                            className={cn(
                              "h-3 w-3",
                              isPopular ? "text-[#F59E0B]" : "text-white/50"
                            )}
                          />
                        ) : (
                          <X className="h-3 w-3 text-white/20" />
                        )}
                      </div>
                      <span
                        className={cn(
                          "text-sm",
                          plan.hasExport ? "text-white/60" : "text-white/30"
                        )}
                      >
                        Export données
                      </span>
                    </li>

                  </ul>

                  {/* Support */}
                  <div className="pt-6 mt-6 border-t border-white/[0.05]">
                    <p className="text-xs text-white/30 mb-1">Support</p>
                    <p className="text-sm text-white/60 font-medium">{plan.support}</p>
                  </div>

                  {/* Link vers détails */}
                  <a
                    href="#comparison-table"
                    className="text-sm text-white/40 hover:text-[#F59E0B] transition-colors mt-4 text-center"
                  >
                    Voir tous les détails →
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default PricingCardsDark;
